"use client"

// Installed from @react-bits/DriftWall-JS-CSS. See react-bits.LICENSE.md.
//
// Local change: a `still` prop. The wall stops drifting and lays the items
// out once, each column centered on the plane, with at most one column per
// item. The drifting wall pads an empty column by repeating the first item,
// which is right for a long list and wrong for a short one: a search that
// leaves one brand would show that brand in every column.
//
// `still` is a request. It holds only while every row fits the wall (one
// row short of its height, which the edge fades eat); a longer list keeps
// drifting, because rows past the edge of a wall that doesn't move would be
// out of reach.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import "./DriftWall.css"

/** @type {Array<{ image: string, title?: string, href?: string }>} */
const DEFAULT_ITEMS = Array.from({ length: 15 }, (_, i) => {
  const ids = [
    1015, 1025, 1039, 1043, 1044, 1050, 1062, 1069, 1074, 1080, 1084, 106, 110,
    133, 164,
  ]
  return {
    image: `https://picsum.photos/id/${ids[i % ids.length]}/600/400`,
    title: `Tile ${i + 1}`,
    href: undefined,
  }
})

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

const columnFactor = (index, variance) => {
  const pseudo = ((index * 0.6180339887 + 0.35) % 1) * 2 - 1
  return 1 + variance * pseudo
}

const DriftWall = ({
  items = DEFAULT_ITEMS,
  columns = 5,
  tileWidth = 200,
  tileHeight = 132,
  gap = 18,
  radius = 14,
  tilt = 16,
  turn = -14,
  roll = 0,
  perspective = 1200,
  depth = 120,
  speed = 42,
  direction = "up",
  variance = 0.45,
  parallax = 0.6,
  pauseOnHover = false,
  lift = 64,
  fade = 0.6,
  dim = 0.55,
  grayscale = false,
  still: stillRequested = false,
  overlayColor = "#060010",
  className = "",
  style = {},
}) => {
  const containerRef = useRef(null)
  const planeRef = useRef(null)
  const trackRefs = useRef([])
  const rafRef = useRef(null)

  const offsetsRef = useRef([])
  const velocitiesRef = useRef([])
  const hoveredColRef = useRef(-1)
  const wallHoveredRef = useRef(false)
  const wallFocusedRef = useRef(false)
  const pointerRef = useRef({ x: 0, y: 0 })
  const pointerDampedRef = useRef({ x: 0, y: 0 })
  const lastTsRef = useRef(null)

  const [containerHeight, setContainerHeight] = useState(600)
  const [activeId, setActiveId] = useState(null)
  const activeIdRef = useRef(null)
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    setReduced(prefersReducedMotion())
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const onChange = (e) => setReduced(e.matches)
    mq.addEventListener("change", onChange)
    return () => mq.removeEventListener("change", onChange)
  }, [])

  const stillColumns = Math.max(1, Math.min(columns, items.length))
  const still =
    stillRequested &&
    Math.ceil(items.length / stillColumns) <=
      Math.max(1, Math.floor(containerHeight / (tileHeight + gap)) - 1)

  const columnItems = useMemo(() => {
    const count = still ? stillColumns : columns
    const cols = Array.from({ length: count }, () => [])
    items.forEach((item, i) => cols[i % count].push(item))
    return cols.map((col) => (col.length ? col : items.slice(0, 1)))
  }, [items, columns, still, stillColumns])

  const columnMeta = useMemo(() => {
    const unit = tileHeight + gap
    return columnItems.map((col) => {
      const copyHeight = Math.max(unit, col.length * unit)
      const copies = still
        ? 1
        : Math.max(2, Math.ceil((containerHeight * 1.6) / copyHeight) + 1)
      return { copyHeight, copies }
    })
  }, [columnItems, tileHeight, gap, containerHeight, still])

  useLayoutEffect(() => {
    if (!containerRef.current) return
    const ro = new ResizeObserver(([entry]) => {
      setContainerHeight(entry.contentRect.height || 600)
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [])

  const baseVelocities = useMemo(() => {
    const dirSign = direction === "up" ? 1 : -1
    return columnItems.map((_, c) => {
      const altSign = c % 2 === 0 ? 1 : -1
      return speed * columnFactor(c, variance) * dirSign * altSign
    })
  }, [columnItems, speed, direction, variance])

  useEffect(() => {
    // Still: a negative offset, which moves the column down, to the middle
    // of the plane (the plane is 1.6 container heights tall).
    offsetsRef.current = columnMeta.map((meta, c) =>
      still
        ? -(containerHeight * 1.6 - meta.copyHeight) / 2
        : meta.copyHeight * ((c * 0.37) % 1)
    )
    velocitiesRef.current = columnItems.map(() => 0)
  }, [columnMeta, columnItems, containerHeight, still])

  const applyPlaneTransform = useCallback(
    (px, py) => {
      const plane = planeRef.current
      if (!plane) return
      plane.style.transform =
        `translate(-50%, -50%) scale(1.18) ` +
        `rotateX(${tilt + py}deg) rotateY(${turn + px}deg) rotateZ(${roll}deg) ` +
        `translateZ(${-depth}px)`
    },
    [tilt, turn, roll, depth]
  )

  useEffect(() => {
    let visible = false
    const animate = (ts) => {
      rafRef.current = null
      if (!visible || document.hidden) return
      if (lastTsRef.current === null) lastTsRef.current = ts
      const dt = Math.min(0.05, Math.max(0, ts - lastTsRef.current) / 1000)
      lastTsRef.current = ts

      const maxTilt = parallax * 8
      const targetX = pointerRef.current.x * maxTilt
      const targetY = -pointerRef.current.y * maxTilt
      const damp = 1 - Math.exp(-dt / 0.12)
      pointerDampedRef.current.x +=
        (targetX - pointerDampedRef.current.x) * damp
      pointerDampedRef.current.y +=
        (targetY - pointerDampedRef.current.y) * damp
      applyPlaneTransform(
        pointerDampedRef.current.x,
        pointerDampedRef.current.y
      )

      if (!reduced && !still) {
        for (let c = 0; c < trackRefs.current.length; c++) {
          const meta = columnMeta[c]
          if (!meta) continue
          const paused =
            (wallHoveredRef.current || wallFocusedRef.current) && pauseOnHover
          const factor = paused || hoveredColRef.current === c ? 0 : 1
          const target = baseVelocities[c] * factor

          const ease = 1 - Math.exp(-dt / (target === 0 ? 0.16 : 0.28))
          velocitiesRef.current[c] += (target - velocitiesRef.current[c]) * ease
          let next =
            (offsetsRef.current[c] ?? 0) + velocitiesRef.current[c] * dt
          next = ((next % meta.copyHeight) + meta.copyHeight) % meta.copyHeight
          offsetsRef.current[c] = next

          const el = trackRefs.current[c]
          if (el) el.style.transform = `translate3d(0, ${-next}px, 0)`
        }
      } else {
        for (let c = 0; c < trackRefs.current.length; c++) {
          const el = trackRefs.current[c]
          const meta = columnMeta[c]
          if (el && meta)
            el.style.transform = `translate3d(0, ${-(offsetsRef.current[c] ?? 0)}px, 0)`
        }
      }

      if (!reduced) rafRef.current = requestAnimationFrame(animate)
    }

    const wake = () => {
      if (visible && !document.hidden && rafRef.current === null) {
        lastTsRef.current = null
        rafRef.current = requestAnimationFrame(animate)
      }
    }
    const visibilityChange = () => {
      if (document.hidden && rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      wake()
    }
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      if (!visible && rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      wake()
    })
    if (containerRef.current) observer.observe(containerRef.current)
    document.addEventListener("visibilitychange", visibilityChange)
    return () => {
      observer.disconnect()
      document.removeEventListener("visibilitychange", visibilityChange)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastTsRef.current = null
    }
  }, [
    baseVelocities,
    columnMeta,
    pauseOnHover,
    parallax,
    reduced,
    still,
    applyPlaneTransform,
  ])

  const activate = useCallback((id, index) => {
    activeIdRef.current = id
    hoveredColRef.current = index
    setActiveId(id)
  }, [])
  const release = useCallback(() => {
    activeIdRef.current = null
    hoveredColRef.current = -1
    setActiveId(null)
  }, [])

  const handlePointerMove = useCallback(
    (e) => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      if (parallax > 0 && !reduced) {
        pointerRef.current = {
          x: (e.clientX - rect.left) / rect.width - 0.5,
          y: (e.clientY - rect.top) / rect.height - 0.5,
        }
      }
      const hit = document.elementFromPoint(e.clientX, e.clientY)
      const tile = hit && hit.closest ? hit.closest("[data-tile-id]") : null
      if (!tile) return
      const id = tile.dataset.tileId
      if (id === activeIdRef.current) return
      activeIdRef.current = id
      hoveredColRef.current = Number(tile.dataset.col)
      setActiveId(id)
    },
    [parallax, reduced]
  )

  const handlePointerLeaveWall = useCallback(() => {
    wallHoveredRef.current = false
    pointerRef.current = { x: 0, y: 0 }
    release()
  }, [release])

  const cssVars = useMemo(
    () => ({
      "--dw-tile-w": `${tileWidth}px`,
      "--dw-tile-h": `${tileHeight}px`,
      "--dw-gap": `${gap}px`,
      "--dw-radius": `${radius}px`,
      "--dw-perspective": `${perspective}px`,
      "--dw-lift": `${lift}px`,
      "--dw-dim": dim,
      "--dw-gray": grayscale ? 1 : 0,
      "--dw-overlay": overlayColor,
      "--dw-edge": `${Math.max(0, (1 - fade) * 100)}%`,
      ...style,
    }),
    [
      tileWidth,
      tileHeight,
      gap,
      radius,
      perspective,
      lift,
      dim,
      grayscale,
      overlayColor,
      fade,
      style,
    ]
  )

  const renderTile = (item, id, colIndex, copyIndex) => {
    const inner = (
      <span className="drift-wall__inner">
        <img
          src={item.image}
          alt={item.title ?? ""}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
        <span className="drift-wall__overlay" aria-hidden="true" />
      </span>
    )
    const commonProps = {
      className: ["drift-wall__tile", activeId === id && "is-active"]
        .filter(Boolean)
        .join(" "),
      "data-tile-id": id,
      "data-col": colIndex,
      "data-copy": copyIndex,
      onFocus: () => activate(id, colIndex),
      onBlur: release,
    }
    if (item.href) {
      return (
        <a key={id} href={item.href} {...commonProps}>
          {inner}
        </a>
      )
    }
    return (
      <div
        key={id}
        tabIndex={0}
        role="button"
        aria-label={item.title ?? "tile"}
        {...commonProps}
      >
        {inner}
      </div>
    )
  }

  const rootClass = [
    "drift-wall",
    reduced ? "drift-wall--reduced" : "",
    still ? "drift-wall--still" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div
      ref={containerRef}
      className={rootClass}
      style={cssVars}
      onPointerMove={handlePointerMove}
      onPointerEnter={() => {
        wallHoveredRef.current = true
      }}
      onPointerLeave={handlePointerLeaveWall}
      onFocusCapture={() => {
        wallFocusedRef.current = true
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          wallFocusedRef.current = false
      }}
      role="group"
      aria-label="Drifting wall of tiles"
    >
      <div
        ref={planeRef}
        className="drift-wall__plane"
        style={{ height: containerHeight * 1.6 }}
      >
        {columnItems.map((col, c) => {
          const meta = columnMeta[c]
          const copies = Array.from({ length: meta.copies })
          return (
            <div className="drift-wall__col" key={`col-${c}`}>
              <div
                className="drift-wall__track"
                ref={(el) => {
                  trackRefs.current[c] = el
                }}
              >
                {copies.map((_, copyIndex) =>
                  col.map((item, itemIndex) =>
                    renderTile(
                      item,
                      still
                        ? `still-${item.href ?? item.title ?? item.image}`
                        : `${c}-${copyIndex}-${itemIndex}`,
                      c,
                      copyIndex
                    )
                  )
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default DriftWall
